/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


 /* 2ten additions */

/* Section: product-subscription-widget.liquid */
document.addEventListener('DOMContentLoaded', function() {
    const productOptions = document.querySelectorAll('.product-subscription--option');
    const checkoutButton = document.querySelector('.btn-checkout');
    let selectedOption = null;

    function updateButton() {
        if (selectedOption) {
            // Enable the button since we have a selection
            checkoutButton.removeAttribute('disabled');
            checkoutButton.classList.remove('is-loading');
        } else {
            checkoutButton.setAttribute('disabled', 'true');
        }
    }

    function updateTotal() {
        const totalDiv = document.querySelector('.product-subscription--total');
        const billingNote = selectedOption.getAttribute('data-billing-note');

        if (selectedOption && totalDiv) {
            const price = selectedOption.getAttribute('data-price');
            const priceCompare = selectedOption.getAttribute('data-price-compare');
            
            let totalHTML = '';
            
            if (priceCompare && parseInt(priceCompare) > parseInt(price)) {
                totalHTML = `
                    <span class="subscription-price">${formatMoney(price)}</span>
                    <span class="subscription-price--compare">${formatMoney(priceCompare)}</span>
                `;
            } else {
                totalHTML = `<span class="subscription-price">${formatMoney(price)}</span>`;
            }
    
            // Add billing note if it exists
            if (billingNote && billingNote.trim() !== '') {
                totalHTML += `<div class="subscription-billing-note">${billingNote}</div>`;
            }
            
            totalDiv.innerHTML = totalHTML;
        }
    }

    function formatMoney(cents) {
        const dollars = (parseInt(cents) / 100).toFixed(2);
        return `$${dollars}`;
    }

    function removeActiveClass() {
        productOptions.forEach(option => {
            option.classList.remove('option--active');
        });
    }

    function selectOption(option) {
        removeActiveClass();
        option.classList.add('option--active');
        selectedOption = option;
        updateButton();
        updateTotal();
    }

    productOptions.forEach(option => {
        option.addEventListener('click', function() {
            selectOption(this);
        });
    });

    // Add to cart functionality
    if (checkoutButton) {
        checkoutButton.addEventListener('click', async function(evt) {
            evt.preventDefault();
            
            if (!selectedOption) {
                console.log('No option selected');
                return;
            }

            // Disable button and show loading state
            this.setAttribute('disabled', 'true');
            this.classList.add('is-loading');

            try {
                const variantId = selectedOption.dataset.variantId;
                const quantity = parseInt(selectedOption.dataset.qty) || 1;
                const sellingPlan = selectedOption.dataset.sellingPlan;

                // Prepare cart add data
                const cartData = {
                    items: [{
                        id: parseInt(variantId),
                        quantity: quantity
                    }]
                };

                // Add selling plan if present (for subscriptions)
                if (sellingPlan) {
                    cartData.items[0].selling_plan = parseInt(sellingPlan);
                }

                // Add to cart using Shopify Cart API
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(cartData)
                });

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(result.description || result.message || 'Failed to add to cart');
                }

                // Trigger cart change event to update cart UI
                document.dispatchEvent(
                    new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
                );

                // Show success state
                this.classList.add('is-success');
 
                // Handle different cart behaviors based on theme settings
                if (theme.settings.afterAddToCart === 'drawer') {
                    // Open cart drawer if theme uses drawer
                    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
                } else if (theme.settings.afterAddToCart === 'page') {
                    // Redirect to cart page
                    setTimeout(() => {
                        window.location.href = '/cart';
                    }, 500);
                } else if (theme.settings.afterAddToCart === 'notification') {
                    // Show notification (theme will handle this via the cart change event)
                    // todo add functionality if needed
                }

                // Reset button state after 2 seconds
                setTimeout(() => {
                    this.classList.remove('is-loading', 'is-success');
                    this.removeAttribute('disabled');
                }, 2000);

            } catch (error) {
                console.error('Error adding to cart:', error);
                
                // Show error message (you can customize this)
                 window.location.href = '/cart';
                // alert('Added');
                
                // Reset button state
                this.classList.remove('is-loading');
                this.removeAttribute('disabled');
            }
        });
    }

    // Select the first option by default (changed from index 1 to 0)
    if (productOptions.length > 0) {
        selectOption(productOptions[0]);
    }
});


/* Product Block Custom: Add to cart functionality */
document.addEventListener('DOMContentLoaded', function() {
    document.addEventListener('click', async function(evt) {
        const button = evt.target.closest('.product-block-add-btn');
        if (!button) return;
        
        evt.preventDefault();
        
        // Check if we're in the cart drawer context
        const inCartDrawer = button.closest('.cart-item-upsells') !== null;
        
        // Get variant ID from button
        const variantId = button.dataset.variantId;
        const productTitle = button.dataset.productTitle;
        
        if (!variantId) {
            console.error('No variant ID found');
            return;
        }
        
        // Set loading state
        const originalText = button.textContent;
        button.disabled = true;
        button.classList.add('is-loading');
        button.textContent = 'Adding...';
        
        try {
            // Add to cart
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    items: [{
                        id: parseInt(variantId),
                        quantity: 1
                    }]
                })
            });
            
            const result = await response.json();
            
            if (!response.ok) {
                throw new Error(result.description || result.message || 'Failed to add to cart');
            }

            // Update cart count
            await updateCartCount();
            
            // Success state
            button.classList.remove('is-loading');
            button.classList.add('is-success');
            button.textContent = 'Added';
            
            // After successful add to cart

            if (inCartDrawer) {
                // Don't refresh drawer - just update count and show success
                await updateCartCount();
                document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
            } else {
                // On PDP, refresh normally
                document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
            }
            
            // Reset button after 2 seconds
            setTimeout(() => {
                button.classList.remove('is-success');
                button.textContent = originalText;
                button.disabled = false;
            }, 2000);
            
        } catch (error) {
            console.error('Error adding to cart:', error);
            
            // Error state
            button.classList.remove('is-loading');
            button.classList.add('is-error');
            button.textContent = 'Error';
            
            // Reset after 2 seconds
            setTimeout(() => {
                button.classList.remove('is-error');
                button.textContent = originalText;
                button.disabled = false;
            }, 2000);
        }
    });
});

/**
 * Update cart count in header
 */
async function updateCartCount() {
    try {
        const response = await fetch('/cart.js', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        const cart = await response.json();
        
        // Update cart count
        const cartCountElements = document.querySelectorAll('.cart-link__count');
        cartCountElements.forEach(el => {
            el.textContent = cart.item_count;
        });
        
    } catch (error) {
        console.error('Error updating cart count:', error);
    }
}